/**
 * Le bundle qu'on execute, et ce qu'on fait quand un morceau a disparu.
 *
 * Le cockpit est decoupe en chunks charges a la demande. Un deploiement
 * renomme ceux qui ont change (Vite les nomme par le hash de leur contenu) et
 * l'image Docker servie ne contient QUE les nouveaux : les anciens noms ne
 * repondent plus. Une fenetre restee ouverte sur le bundle d'avant demande
 * donc, au moment ou son porteur ouvre un panneau qu'il n'avait pas encore
 * ouvert, un fichier qui n'existe plus.
 *
 * 🪤 Tant que le service worker rechargeait la page TOUT SEUL, ce trou etait
 * invisible : personne ne restait assez longtemps sur un bundle perime pour le
 * rencontrer. Depuis qu'on ne recharge plus de force un chatteur en plein
 * message (cf `PwaUpdateToast`), il devient atteignable — d'ou ce filet, qui
 * fait partie du meme correctif et pas d'un autre.
 *
 * Le self-heal inline d'`index.html` ne couvre PAS ce cas : il ecoute l'evenement
 * `error` d'une balise `<script src>`, ce qui attrape la page blanche au boot
 * (index.html en cache pointant vers un bundle supprime). Un `import()`
 * dynamique qui echoue, lui, REJETTE une promesse — aucune balise, aucun
 * evenement `error`. Deux symptomes, deux filets.
 */

/**
 * Le chunk d'entree, extrait d'un HTML ou d'une URL. Vite le nomme avec le
 * hash de son contenu : deux builds differents ne peuvent pas le partager.
 */
const MOTIF_ENTREE = /\/assets\/(index-[A-Za-z0-9_-]+\.js)/;

export function chunkDe(source: string): string | null {
  return MOTIF_ENTREE.exec(source)?.[1] ?? null;
}

/**
 * Le chunk que CETTE page execute. `null` en dev (l'entree est `/src/main.tsx`,
 * pas un asset hashe) et dans tout contexte ou la balise manque — on preferera
 * toujours ne rien faire a faire quelque chose de faux.
 */
export function chunkCharge(): string | null {
  const el = document.querySelector<HTMLScriptElement>(
    'script[type="module"][src*="/assets/index-"]',
  );
  return el == null ? null : chunkDe(el.src);
}

/**
 * Les facons dont les navigateurs disent « ce module ne se charge pas ». Aucune
 * n'est normalisee, d'ou la liste : Chrome, Firefox et Safari ont chacun la
 * leur, et le 4e cas est le SPA fallback de nginx qui rend `index.html` (donc
 * du `text/html`) a la place du JS demande.
 */
const MOTIFS_CHUNK_PERDU = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
  /failed to load module script/i,
];

export function estChunkPerdu(e: unknown): boolean {
  const message =
    e instanceof Error ? e.message : typeof e === "string" ? e : "";
  return message !== "" && MOTIFS_CHUNK_PERDU.some((m) => m.test(message));
}

/**
 * Clef d'anti-boucle. On y range le nom du chunk d'entree AVEC lequel on a
 * deja tente un rechargement.
 *
 * 🪤 Un booleen ne conviendrait pas, et le flag d'`index.html` non plus :
 * `main.tsx` efface `pwa-stale-reload` a chaque boot reussi, donc s'en servir
 * ici rendrait la garde inoperante des le rechargement suivant — une boucle,
 * exactement celle payee le 16-07 sur /candidature. En rangeant le nom du
 * bundle, on s'autorise UN rechargement par version chargee : si celui-ci ne
 * repare pas, on n'insiste pas ; et le deploiement d'apres, qui nous met sur un
 * autre chunk, redonne droit a une tentative.
 */
const CLE_TENTATIVE = "pwa-chunk-reload";

/**
 * Recharge une fois si `e` est un chunk disparu. Rend `true` si le
 * rechargement est lance (l'appelant peut alors se taire : la page s'en va).
 */
export function rechargerSiChunkPerdu(e: unknown): boolean {
  if (!estChunkPerdu(e)) return false;
  // En dev le serveur reconstruit a la volee : un import qui echoue est une
  // vraie erreur de code, la masquer par un rechargement serait pire.
  const notre = chunkCharge();
  if (notre == null) return false;
  try {
    if (sessionStorage.getItem(CLE_TENTATIVE) === notre) return false;
    sessionStorage.setItem(CLE_TENTATIVE, notre);
  } catch {
    return false; // sessionStorage indispo → on ne risque pas une boucle
  }
  window.location.reload();
  return true;
}
